import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  IOutboxEmail,
  IOutboxEmailPageResponse,
} from 'src/services/api/EmailOutbox/apiContracts';
import { fetchRecentOutboxEmails, isApiError } from 'src/services/client';
import { useEmailOutboxSocket } from 'src/pages/Outbox/CustomHooks/useEmailOutboxSocket';
import {
  appendOutboxPage,
  describeOutboxLoadError,
  getSocketIndicator,
  mergeUpdatedOutboxEmails,
  OUTBOX_PAGE_SIZE,
} from 'src/pages/Outbox/helpers';
import {
  IOutboxSocketIndicator,
  IUseOutboxPageOrchestratorReturn,
  OutboxSocketState,
} from 'src/pages/Outbox/types';

const LOAD_MORE_ERROR: string = 'Could not load more emails. Please try again.';

export const useOutboxPageOrchestrator =
  (): IUseOutboxPageOrchestratorReturn => {
    const [emails, setEmails] = useState<IOutboxEmail[]>([]);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const loadFirstPage = async (): Promise<void> => {
      try {
        const response: any = await fetchRecentOutboxEmails(
          0,
          OUTBOX_PAGE_SIZE
        );

        if (isApiError(response)) {
          setError(describeOutboxLoadError(response));
          return;
        }

        const { data }: IOutboxEmailPageResponse = await response.json();

        setError(null);
        setEmails(data.items);
        setHasMore(data.hasMore);
      } catch (loadError) {
        console.error('Error loading the email outbox:', loadError);
        setError(describeOutboxLoadError(null));
      }
    };

    const refresh = async (): Promise<void> => {
      setIsRefreshing(true);

      await loadFirstPage();

      setIsRefreshing(false);
    };

    const loadMore = async (): Promise<void> => {
      setIsLoadingMore(true);

      try {
        const response: any = await fetchRecentOutboxEmails(
          emails.length,
          OUTBOX_PAGE_SIZE
        );

        if (isApiError(response)) {
          toast.error(LOAD_MORE_ERROR);
          return;
        }

        const { data }: IOutboxEmailPageResponse = await response.json();

        setEmails((current: IOutboxEmail[]) =>
          appendOutboxPage(current, data.items)
        );
        setHasMore(data.hasMore);
      } catch (loadError) {
        console.error('Error loading more outbox emails:', loadError);
        toast.error(LOAD_MORE_ERROR);
      } finally {
        setIsLoadingMore(false);
      }
    };

    const handleEmailsUpdated = (updates: IOutboxEmail[]): void => {
      setEmails((current: IOutboxEmail[]) =>
        mergeUpdatedOutboxEmails(current, updates)
      );
    };

    const socketState: OutboxSocketState = useEmailOutboxSocket({
      onEmailsUpdated: handleEmailsUpdated,
      onReconnected: refresh,
    });

    const socketIndicator: IOutboxSocketIndicator =
      getSocketIndicator(socketState);

    useEffect(() => {
      const loadInitialPage = async (): Promise<void> => {
        await loadFirstPage();
        setIsLoading(false);
      };

      void loadInitialPage();
    }, []);

    return {
      emails,
      hasMore,
      isLoading,
      isRefreshing,
      isLoadingMore,
      error,
      socketIndicator,
      refresh,
      loadMore,
    };
  };
